import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWith401Handling } from '../shared/api/baseQueryWith401Handling';
import Project from './models/Project';
import ProjectOverview from './models/ProjectOverview';
import UpdateProjectModel from './models/UpdateProjectModel';
import UpdateAreaModel from './models/UpdateAreaModel';
import { Area } from './models/Area';
import UpdateCurrentSetupModel from './models/UpdateCurrentSetupModel';
import UpdateReferenceStationModel from './models/UpdateReferenceStationModel';
import UpdateProposalProductModel from './models/UpdateProposalProductModel';
import { Product } from './models/Product';
import DeleteProposalProductModel from './models/DeleteProposalProductModel';
import UpdateGeneralInfoModel from './models/UpdateGeneralInfoModel';
import ExportProjectModel from './models/ExportProjectModel';
import SaveAreasModel from './models/SaveAreasModel';
import UpdateAccountLevelModel from './models/UpdateAccountLevelModel';
import AccountLevel from './models/AccountLevel';
import DashboardItem from './models/DashboardItem';
import MoveProjectToAccountLevelModel from './models/MoveProjectToAccountLevelModel';
import { PricingFinanceModel } from './models/PricingFinanceModel';
import { UpdateEquipmentPricesModel } from './models/UpdateEquipmentPricesModel';
import { projectSlice } from './projectSlice';
import { ProposalProduct } from './models/ProposalProduct';

const handleSavedState = async (dispatch: any, queryFulfilled: Promise<any>) => {
    try {

        await queryFulfilled;
        dispatch(projectSlice.actions.setIsSaving(true));
        setTimeout(() => {
            dispatch(projectSlice.actions.setIsSaving(false));
        }, 1000);
        dispatch(projectSlice.actions.setSaved(true));
        /* setTimeout(() => {
            dispatch(projectSlice.actions.setSaved(false));
        }, 3000); */

    } catch {
        dispatch(projectSlice.actions.setShowError(true));
    }
};

export const projectApi = createApi({
    reducerPath: 'projectApi',
    baseQuery: baseQueryWith401Handling,
    tagTypes: ['Projects', 'Project', 'Products'],
    endpoints: (builder) => ({
        getAllProjects: builder.query<DashboardItem[], void>({
            query: () => ({
                url: "projects"
            }),
            providesTags: ['Projects']
        }),
        getProject: builder.query<Project, string>({
            query: (id) => ({
                url: `projects/${id}`
            }),
            providesTags: () => ['Project']
        }),
        createProject: builder.mutation<ProjectOverview, UpdateProjectModel>({
            query: (project) => ({
                url: `projects/create`,
                method: 'POST',
                body: project
              }),
              invalidatesTags: ['Projects'],
              async onQueryStarted(_, { dispatch, queryFulfilled }) {
                await handleSavedState(dispatch, queryFulfilled);
            }
        }),
        updateProject: builder.mutation<ProjectOverview, UpdateProjectModel>({
            query: (project) => ({
                url: `projects/${project.id}/update`,
                method: 'POST',
                body: project
              }),
              invalidatesTags: ['Projects', 'Project', 'Products'],
              async onQueryStarted(_, { dispatch, queryFulfilled }) {
                await handleSavedState(dispatch, queryFulfilled);
            }
        }),
        copyProject: builder.mutation<ProjectOverview, string>({
            query: (id) => ({
                url: `projects/${id}/copy`,
                method: 'POST'
              }),
              invalidatesTags: ['Projects']
        }),
        deleteProject: builder.mutation<void, string>({
            query: (id) => ({
                url: `projects/${id}/delete`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Projects', 'Project']
        }),
        saveAreas: builder.mutation<void, SaveAreasModel>({
            query: (model) => ({
                url: `projects/${model.projectId}/areas/save`,
                method: 'POST',
                body: model
            }),
            invalidatesTags: () => ['Projects', 'Project'],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                await handleSavedState(dispatch, queryFulfilled);
            }
        }),
        updateArea: builder.mutation<Area, UpdateAreaModel>({
            query: (model) => ({
                url: `projects/${model.projectId}/areas/update`,
                method: 'POST',
                body: model
            }),
            invalidatesTags: () => ['Project'],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                await handleSavedState(dispatch, queryFulfilled);
            }
        }),
        createCurrentSetup: builder.mutation<Project, UpdateCurrentSetupModel>({
            query: (model) => ({
                url: `projects/${model.projectId}/currentSetup/create`,
                method: 'POST',
                body: model
              }),
              async onQueryStarted(model, { dispatch, queryFulfilled }) {
                await handleSavedState(dispatch, queryFulfilled);
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        projectApi.util.updateQueryData('getProject', model.projectId, () => data)
                    );
                } catch {}
            }
        }),
        updateReferenceStation: builder.mutation<Project, UpdateReferenceStationModel>({
            query: (model) => ({
                url: `projects/${model.projectId}/referenceStation/update`,
                method: 'POST',
                body: model
            }),
            invalidatesTags: () => ['Project'],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                await handleSavedState(dispatch, queryFulfilled);
            }
        }),
        deleteProposalProduct: builder.mutation<void, DeleteProposalProductModel>({
            query: (model) => ({
                url: `projects/${model.projectId}/mowers/delete/`,
                method: 'POST',
                body: model
            }),
            invalidatesTags: () => ['Project', 'Products'],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                await handleSavedState(dispatch, queryFulfilled);
            }
        }),
        saveProposalProduct: builder.mutation<ProposalProduct | undefined, UpdateProposalProductModel>({
            query: (model) => ({
                url: `projects/${model.projectId}/mowers/save`,
                method: 'POST',
                body: model
            }),
            invalidatesTags: () => ['Project', 'Products'],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                await handleSavedState(dispatch, queryFulfilled);
            }
        }),
        getAllProducts: builder.query<Product[], string>({
            query: (id) => ({
                url: `projects/${id}/products`,
                method: "GET",
            }),
            providesTags: ['Products']
        }),
        updateGeneralInfo: builder.mutation<Project, UpdateGeneralInfoModel>({
            query: (model) => ({
                url: `projects/${model.projectId}/generalInfo/update`,
                method: 'POST',
                body: model
            }),
            async onQueryStarted(model, { dispatch, queryFulfilled }) {
                await handleSavedState(dispatch, queryFulfilled);
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        projectApi.util.updateQueryData('getProject', model.projectId, () => data)
                    );
                } catch {}
            }
        }),
        saveProjectFinance: builder.mutation<Project, PricingFinanceModel>({
            query: (model) => ({
                url: `projects/${model.projectId}/finance/save`,
                method: 'POST',
                body: model
            }),
            async onQueryStarted(model, { dispatch, queryFulfilled }) {
                await handleSavedState(dispatch, queryFulfilled);
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        projectApi.util.updateQueryData('getProject', model.projectId, () => data)
                    );
                } catch {}
            }
        }),
        saveEquipmentPrices: builder.mutation<void, UpdateEquipmentPricesModel>({
            query: (model) => ({
                url: `projects/${model.projectId}/equipmentPrices/save`,
                method: 'POST',
                body: model
            }),
            invalidatesTags: () => ['Project'],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                await handleSavedState(dispatch, queryFulfilled);
            }
        }),
        saveAccountLevel: builder.mutation<AccountLevel, UpdateAccountLevelModel>({
            query: (model) => ({
                url: `projects/accountLevel/save`,
                method: 'POST',
                body: model
            }),
            invalidatesTags: () => ["Projects"]
        }),
        deleteAccountLevel: builder.mutation<void, string>({
            query: (id) => ({
                url: `projects/accountLevel/${id}/delete`,
                method: 'POST',
            }),
            invalidatesTags: () => ["Projects"]
        }),
        moveProjectToAccountLevel: builder.mutation<void, MoveProjectToAccountLevelModel>({
            query: (model) => ({
                url: `projects/accountLevel/${model.projectId}/move/`,
                method: 'POST',
                body: model
            }),
            invalidatesTags: () => ["Projects"]
        }),
        removeAccountLevelFromProject: builder.mutation<void, string>({
            query: (id) => ({
                url: `projects/accountLevel/${id}/remove/`,
                method: 'POST',
            }),
            invalidatesTags: () => ["Projects"]
        })
    }),
});

export const { 
    useGetAllProjectsQuery,
    useGetProjectQuery,
    useCreateProjectMutation,
    useUpdateProjectMutation,
    useCopyProjectMutation,
    useDeleteProjectMutation,
    useSaveAreasMutation,
    useUpdateAreaMutation,
    useCreateCurrentSetupMutation,
    useUpdateReferenceStationMutation,
    useDeleteProposalProductMutation,
    useSaveProposalProductMutation,
    useGetAllProductsQuery,
    useUpdateGeneralInfoMutation,
    useSaveProjectFinanceMutation,
    useSaveEquipmentPricesMutation,
    useSaveAccountLevelMutation,
    useDeleteAccountLevelMutation,
    useMoveProjectToAccountLevelMutation,
    useRemoveAccountLevelFromProjectMutation,
} = projectApi;
